import { Injectable, OnDestroy } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  mergeMap,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';

import { AppState, IToken } from '../shared/interfaces/utils.interface';
import {
  selectGetToken,
  selectStoredAuth,
} from '../_store/auth/auth.selectors';
import { updateToken } from '../_store/auth/auth.action';
import { setUserInfo } from '../_store/user/user.actions';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private userService: UserService,
    private store: Store<AppState>,
  ) {}
  token!: IToken;
  refreshUrl = '/auth/refresh';
  private refreshingToken = false; // Flag to track if token refresh is in progress

  // url which wants to cache data
  private urlToCache = [];

  // Map of caching data key: value pair key === url / value === data
  private responseCache = new Map<
    string,
    { response: HttpEvent<any>; timestamp: number }
  >();
  // timestamp to set cache life
  private cacheExpirationTime = 1800000; // 30 minutes in milliseconds

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const cache = this.responseCache.get(request.urlWithParams);
    // if cache return data which already have
    if (cache && this.isCacheValid(cache.timestamp)) {
      return of(cache.response);
    }

    // use store selector of Ngrx to get token in stored and sent it with header
    return this.store.select(selectGetToken).pipe(
      take(1),
      mergeMap((token: IToken) => {
        if (token && !request.url.includes(this.refreshUrl)) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          });
        }

        if (!this.canCache(request)) {
          return next
            .handle(request)
            .pipe(
              catchError((error: HttpErrorResponse) =>
                this.errorAuthHandle(error, request, next),
              ),
            );
        } else {
          return next.handle(request).pipe(
            tap((res) => {
              this.responseCache.set(request.urlWithParams, {
                response: res,
                timestamp: Date.now(),
              });
            }),
            catchError((error: HttpErrorResponse) =>
              this.errorAuthHandle(error, request, next),
            ),
          );
        }
      }),
    );
  }

  private canCache(request: HttpRequest<unknown>) {
    return this.urlToCache.some((keyword) =>
      request.urlWithParams.includes(keyword),
    );
  }

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.cacheExpirationTime;
  }

  private errorAuthHandle(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ) {
    if (this.isRefreshRequest(request) && this.isTerminalAuthFailure(error)) {
      this.forceLogout();
      return EMPTY;
    }

    const statusCode = error.error?.statusCode;
    const statusMessage = error.error?.statusMessage;
    if (
      (error.status === 401 || statusCode === 401) &&
      statusMessage === 'JWT Expired.'
    ) {
      // return method to get new token

      return this.refreshTokenMethod(request, next);
    } else if (
      error.error instanceof Blob &&
      error.statusText === 'Unauthorized'
    ) {
      return this.refreshTokenMethod(request, next);
    } else if (
      (error.status === 401 || statusCode === 401) &&
      (statusMessage === 'AccessToken is invalid.' ||
        statusMessage === 'RefreshToken is invalid.')
    ) {
      this.forceLogout();

      return EMPTY;
    }

    return throwError((): any => error);
  }

  private refreshTokenMethod(request: HttpRequest<unknown>, next: HttpHandler) {
    if (!this.refreshingToken) {
      this.refreshingToken = true; // Set flag to indicate token refresh is in progress
      this.authService.notifyRefreshTokenUpdated(null);

      return this.authService.RefreshToken().pipe(
        switchMap((res: any) => {
          const token = {
            accessToken: res.data.accessToken,
            refreshToken: res.data.refreshToken,
          };
          this.store.dispatch(updateToken({ token: token }));
          this.authService.notifyRefreshTokenUpdated(res.data.accessToken);

          request = request.clone({
            setHeaders: { Authorization: 'Bearer ' + res.data.accessToken },
          });

          return this.store.select(selectStoredAuth).pipe(
            take(1),
            tap((user) => {
              if (!this.shouldRefreshMerchantProfile(user)) return;
              this.refreshMerchantProfile();
            }),
            switchMap(() => {
              if (!this.canCache(request)) return next.handle(request);
              return next.handle(request).pipe(
                tap((retryResponse) => {
                  this.responseCache.set(request.urlWithParams, {
                    response: retryResponse,
                    timestamp: Date.now(),
                  });
                }),
              );
            }),
          );
        }),
        catchError((error: HttpErrorResponse) => {
          // Reset the flag if there's an error during token refresh

          this.refreshingToken = false;

          if (this.isTerminalAuthFailure(error)) {
            this.forceLogout();

            return EMPTY;
          } else {
            return throwError(() => error);
          }
        }),
        finalize(() => {
          this.refreshingToken = false;
        }),
      );
    } else {
      // If a refresh token request is already in progress, wait for it to complete

      return this.authService.refreshTokenObservable.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((): Observable<HttpEvent<any>> => {
          return this.intercept(request, next);
        }),
      );
    }
  }

  private shouldRefreshMerchantProfile(
    user: { roles?: unknown; canAccessFreeOrganizer?: boolean } | null,
  ): boolean {
    return (
      !!user && user.canAccessFreeOrganizer !== true && user.roles !== 'USER'
    );
  }

  private isRefreshRequest(request: HttpRequest<unknown>): boolean {
    return request.url.includes(this.refreshUrl);
  }

  private isTerminalAuthFailure(error: HttpErrorResponse | any): boolean {
    const status =
      error?.status ?? error?.statusCode ?? error?.error?.statusCode;
    return status === 401 || status === 403;
  }

  private refreshMerchantProfile(): void {
    this.userService
      .initMerchant()
      .pipe(take(1))
      .subscribe({
        next: (merchantRes) => {
          if (
            merchantRes.data?.merchant?.sportType &&
            merchantRes.data.merchant.sportType.length === 1
          ) {
            const sportType = merchantRes.data.merchant.sportType;
            const sportTypeList = sportType[0].split(',');
            if (sportTypeList.length > 1) {
              merchantRes.data.merchant.sportType = sportTypeList;
            }
          }
          this.store.dispatch(
            setUserInfo({
              user: { ...merchantRes.data, image: merchantRes.data.image },
            }),
          );
        },
      });
  }

  forceLogout() {
    this.authService.clearLocalSession();
  }
}
